export const PUBLIC_PROFILE_STATUS = Object.freeze({
  PRIVATE: 'private',
  DRAFT: 'draft',
  PUBLISHED: 'published',
});

export const PUBLIC_PROFILE_STATUSES = Object.freeze(Object.values(PUBLIC_PROFILE_STATUS));

export const PUBLIC_PROFILE_VISIBILITY_DEFAULT = Object.freeze({
  identity: true,
  social_links: false,
  library: false,
  reviews: false,
  gamification: false,
  live_status: false,
  streamer_profile: false,
  activity: false,
});

export const PUBLIC_PROFILE_VISIBILITY_KEYS = Object.freeze(
  Object.keys(PUBLIC_PROFILE_VISIBILITY_DEFAULT),
);

export const PUBLIC_HANDLE_PATTERN = /^[a-z0-9][a-z0-9_-]{1,28}[a-z0-9]$/;

export const PUBLIC_PROFILE_SELECT = [
  'public_handle',
  'public_profile_status',
  'public_profile_visibility',
  'public_profile_published_at',
  'public_profile_handle_updated_at',
  'is_public',
].join(', ');

export const PUBLIC_PROFILE_SCHEMA_COLUMNS = PUBLIC_PROFILE_SELECT.split(', ')
  .filter((column) => column !== 'is_public');

const hasOwn = (payload, key) =>
  Boolean(payload && Object.prototype.hasOwnProperty.call(payload, key));

export class PublicProfileValidationError extends Error {
  constructor(message, code = 'invalid_public_profile') {
    super(message);
    this.name = 'PublicProfileValidationError';
    this.code = code;
  }
}

export const isPublicProfileStatus = (value) =>
  PUBLIC_PROFILE_STATUSES.includes(String(value || '').trim().toLowerCase());

export const normalizePublicProfileStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();
  return isPublicProfileStatus(status) ? status : PUBLIC_PROFILE_STATUS.PRIVATE;
};

export const normalizePublicHandle = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const handle = value.trim().toLowerCase();
  return handle.length > 0 ? handle : null;
};

export const assertValidPublicHandle = (handle) => {
  if (handle === null) {
    return null;
  }
  if (!PUBLIC_HANDLE_PATTERN.test(handle)) {
    throw new PublicProfileValidationError(
      'Handle pubblico non valido.',
      'invalid_public_handle',
    );
  }
  return handle;
};

export const normalizePublicProfileVisibility = (payload = {}) => {
  const defaults = PUBLIC_PROFILE_VISIBILITY_DEFAULT;
  const source = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? payload
    : {};

  return PUBLIC_PROFILE_VISIBILITY_KEYS.reduce((acc, key) => {
    acc[key] =
      typeof source[key] === 'boolean'
        ? source[key]
        : defaults[key];
    return acc;
  }, {});
};

export const normalizePublicProfile = (payload = {}) => {
  const handle = normalizePublicHandle(payload?.public_handle ?? payload?.handle);
  const status = normalizePublicProfileStatus(
    payload?.public_profile_status ?? payload?.status,
  );

  return {
    handle,
    status,
    visibility: normalizePublicProfileVisibility(
      payload?.public_profile_visibility ?? payload?.visibility,
    ),
    isPublic: status === PUBLIC_PROFILE_STATUS.PUBLISHED && Boolean(handle),
    publishedAt: payload?.public_profile_published_at ?? payload?.publishedAt ?? null,
    handleUpdatedAt:
      payload?.public_profile_handle_updated_at ?? payload?.handleUpdatedAt ?? null,
    legacyIsPublic: Boolean(payload?.is_public),
  };
};

export const hasPublicProfileSchemaError = (error) => {
  const message = String(error?.message || error?.details || '').toLowerCase();
  return (
    error?.code === 'PGRST204' ||
    error?.code === '42703' ||
    PUBLIC_PROFILE_SCHEMA_COLUMNS.some((column) => message.includes(column))
  );
};

export const buildPublicProfileMutation = (input = {}, currentPayload = {}) => {
  const current = normalizePublicProfile(currentPayload);
  const next = {
    handle: current.handle,
    status: current.status,
    visibility: current.visibility,
  };

  if (hasOwn(input, 'handle') || hasOwn(input, 'publicHandle') || hasOwn(input, 'public_handle')) {
    next.handle = assertValidPublicHandle(
      normalizePublicHandle(input.handle ?? input.publicHandle ?? input.public_handle),
    );
  }

  if (hasOwn(input, 'status') || hasOwn(input, 'publicProfileStatus') || hasOwn(input, 'public_profile_status')) {
    const status = String(
      input.status ?? input.publicProfileStatus ?? input.public_profile_status ?? '',
    ).trim().toLowerCase();
    if (!isPublicProfileStatus(status)) {
      throw new PublicProfileValidationError(
        'Stato profilo pubblico non valido.',
        'invalid_public_profile_status',
      );
    }
    next.status = status;
  }

  if (hasOwn(input, 'visibility') || hasOwn(input, 'publicProfileVisibility') || hasOwn(input, 'public_profile_visibility')) {
    next.visibility = normalizePublicProfileVisibility(
      input.visibility ?? input.publicProfileVisibility ?? input.public_profile_visibility,
    );
  }

  if (next.status === PUBLIC_PROFILE_STATUS.PUBLISHED && !next.handle) {
    throw new PublicProfileValidationError(
      'Serve un handle prima di pubblicare il profilo.',
      'public_handle_required',
    );
  }

  return {
    public_handle: next.handle,
    public_profile_status: next.status,
    public_profile_visibility: next.visibility,
  };
};
